import { ClassNames } from '@emotion/react';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useRef } from 'react';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { statusToneStyle, UDStatusTone } from 'ud-ui/components/status-tone';
import UDText from 'ud-ui/components/text';
import { calendarStyles } from './calendar-styles';

/** Число под числом дня: цифра, тон цвета и подсказка при наведении. */
export interface UDCalendarDayBadge {
  text: string;
  /** Тон цифры из словаря кита (`ud-ui/components/status-tone`); без него — приглушённый. */
  tone?: UDStatusTone;
  /** Подсказка над ячейкой дня: цвет — не единственный признак, смысл цифры должен быть и текстом. */
  title?: string;
}

export interface UDCalendarProps {
  /** Выбранный день. */
  selected?: Date | null;
  onChange: (date: Date) => void;
  /**
   * Месяц, который показан первым. Смена пропса перелистывает календарь (react-datepicker 4.25 следит за ним и после
   * монтирования): так владелец экрана листает стрелками «назад» / «вперёд» и «Сегодня». Переход стрелками клавиатуры
   * внутри календаря владелец узнаёт из `onShownMonthChange`.
   */
  openToDate?: Date;
  /** Сколько месяцев показано рядом. */
  monthsShown?: number;
  /** Всегда шесть недель в месяце: высота календаря не прыгает при листании. */
  fixedHeight?: boolean;
  /**
   * Календарь встроен в карточку экрана: без своих рамки и тени, месяцы делят ширину блока поровну,
   * а ячейки растягиваются. Без него — карточка в 40 px на ячейку, как у поповера поля даты.
   */
  embedded?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /**
   * Дни с событиями: жёлтая обводка ячейки и точка под числом (в `aria-hidden`, смысл — на экране рядом с календарём).
   * Обводка нужна, чтобы такие дни находились взглядом сразу; у прошедших дней она серая, выбранный день залит жёлтым и
   * обводки не показывает.
   */
  isMarked?: (date: Date) => boolean;
  /**
   * Число под числом дня (сколько событий): ячейка становится выше (44 вместо 36 px). Перекрывает точку `isMarked` у
   * того же дня. В `aria-label` дня оно не попадает — его собирает библиотека, — поэтому смысл должен быть ещё и в `title`.
   */
  dayBadge?: (date: Date) => UDCalendarDayBadge | null | undefined;
  /** Своё содержимое дня вместо числа. Перекрывает `isMarked` и `dayBadge`. */
  renderDayContents?: (dayOfMonth: number, date?: Date) => React.ReactNode;
  /**
   * Со стороны какого края календарь появляется при монтировании (200 мс, сдвиг 12 px, без движения при
   * `prefers-reduced-motion`). Владелец листает, пересоздавая календарь по `key`, и ставит сторону по направлению листания.
   */
  enterFrom?: 'left' | 'right';
  /**
   * Сработает, когда сменился **первый показанный** месяц — кнопкой владельца (`openToDate`) или стрелкой клавиатуры за
   * край окна, когда библиотека сама сдвигает его. Клик по дню второго показанного месяца её не вызывает: окно стоит на
   * месте. Библиотечный `onMonthChange` для этого не годится — он приходит и в том случае, и сообщает месяц нового дня.
   */
  onShownMonthChange?: (month: Date) => void;
  className?: string;
}

const capitalize = (text: string, locale: string) => `${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1)}`;

/**
 * Сообщает владельцу первый показанный месяц. Живёт внутри шапки первого месяца: библиотека перерисовывает свой
 * календарь сама (стрелка клавиатуры двигает окно), а `UDCalendar` при этом может не перерисоваться — эффект на нём
 * изменения не увидел бы. Первое значение запоминается и не сообщается: владелец его знает (`openToDate`).
 *
 * Сообщает отложенно (`setTimeout 0`): клик по дню второго месяца библиотека обрабатывает двумя перерисовками, и между
 * ними шапка на кадр показывает не тот месяц (сентябрь вместо октября). Отложенный отчёт видит только устоявшееся значение.
 */
const ShownMonthReporter = ({ month, onChange }: { month: Date; onChange?: (month: Date) => void }) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const key = year * 12 + monthIndex;
  const reported = useRef(key);

  useEffect(() => {
    if (reported.current === key) return undefined;

    const timer = setTimeout(() => {
      reported.current = key;
      onChange?.(new Date(year, monthIndex, 1));
    }, 0);

    return () => clearTimeout(timer);
  }, [key, year, monthIndex, onChange]);

  return null;
};

/**
 * Календарь кита без поля ввода — месяц (или несколько) прямо на странице, на том же `react-datepicker` и
 * тех же `calendarStyles`, что и поповер `UDDateInput`: выбранный день жёлтый, «сегодня» — золотая рамка, кольцо фокуса
 * штатное, дни соседних месяцев скрыты. С клавиатуры — одна остановка Tab на календарь и переход стрелками, как в
 * шаблоне сетки дат WAI-ARIA APG.
 *
 * Шапка у него упрощённая — название месяца по центру: листание, год и «Сегодня» остаются у владельца экрана
 * (`openToDate`). Шапку с переключателем «месяц → год» из `UDDateInput` для нескольких месяцев не взять:
 * стрелки в ней одни на календарь, а не на месяц.
 */
const UDCalendar = ({
  selected,
  onChange,
  openToDate,
  monthsShown = 1,
  fixedHeight = true,
  embedded = false,
  minDate,
  maxDate,
  isMarked,
  dayBadge,
  renderDayContents,
  enterFrom,
  onShownMonthChange,
  className,
}: UDCalendarProps) => {
  const { t, i18n } = useLingui();

  const renderHeader = useCallback(
    ({ monthDate, customHeaderCount }: ReactDatePickerCustomHeaderProps) => (
      <>
        {customHeaderCount === 0 && <ShownMonthReporter month={monthDate} onChange={onShownMonthChange} />}
        <UDText variant="body-3" as="div" className="flex h-7 items-center justify-center" data-testid="ud-calendar-month-title">
          {capitalize(monthDate.toLocaleString(i18n.locale, { month: 'long' }), i18n.locale)} {monthDate.getFullYear()}
        </UDText>
      </>
    ),
    [i18n.locale, onShownMonthChange],
  );

  // Прошедший день с событиями — серая обводка вместо жёлтой: жёлтое остаётся за тем, что ещё впереди или идёт сегодня.
  const dayClassName = useCallback(
    (date: Date) => {
      if (!isMarked?.(date)) return '';
      const startOfToday = new Date().setHours(0, 0, 0, 0);
      return date.getTime() < startOfToday ? 'ud-calendar-day--marked ud-calendar-day--past' : 'ud-calendar-day--marked';
    },
    [isMarked],
  );

  const dayContents = useCallback(
    (dayOfMonth: number, date?: Date) => {
      if (renderDayContents) return renderDayContents(dayOfMonth, date);

      const badge = date ? dayBadge?.(date) : undefined;
      return (
        <span className="ud-calendar-day-content" title={badge?.title}>
          {dayOfMonth}
          {badge && (
            <span className="ud-calendar-count" style={badge.tone ? statusToneStyle(badge.tone) : undefined} aria-hidden>
              {badge.text}
            </span>
          )}
          {!badge && date && isMarked?.(date) && <span className="ud-calendar-mark" data-testid="ud-calendar-mark" aria-hidden />}
        </span>
      );
    },
    [dayBadge, isMarked, renderDayContents],
  );

  return (
    <div data-testid="ud-calendar" data-embedded={embedded} data-enter={enterFrom}>
      <ClassNames>
        {({ css, cx, theme }) => (
          <DatePicker
            inline
            selected={selected ?? null}
            onChange={(date) => date && onChange(date)}
            openToDate={openToDate}
            monthsShown={monthsShown}
            calendarStartDay={1}
            fixedHeight={fixedHeight}
            minDate={minDate}
            maxDate={maxDate}
            renderCustomHeader={renderHeader}
            chooseDayAriaLabelPrefix={t`Выбрать`}
            disabledDayAriaLabelPrefix={t`Недоступно`}
            dayClassName={dayClassName}
            renderDayContents={dayContents}
            calendarClassName={cx(
              css(calendarStyles(theme)),
              {
                'ud-calendar--embedded': embedded,
                'ud-calendar--tall': Boolean(dayBadge),
                'ud-calendar--enter-left': enterFrom === 'left',
                'ud-calendar--enter-right': enterFrom === 'right',
              },
              className,
            )}
          />
        )}
      </ClassNames>
    </div>
  );
};

export default UDCalendar;
